'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import AlertTriangle from '@ab-inbev-eu/ui-library/components/assets/icons/AlertTriangle';
import RotateCw from '@ab-inbev-eu/ui-library/components/assets/icons/RotateCw';

import { Track } from '../../../../components/analytics/track';
import { useTranslation } from '../../../../i18n/client';
import { CampaignBackground } from '../../../../components/CampaignBackground';

import { LoadingButtonLink } from './_components/LoadingButtonLink';

export default function RootPageError({ error }: { error: Error }) {
  const { qrId }: { qrId: string } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    datadogRum.addError(error, {
      error_message: error.message,
      screen: 'ErrorBoundary',
    });
  }, [error]);

  return (
    <CampaignBackground>
      <div className="font-body [&]:text-on-background absolute left-0 top-0 box-border flex size-full max-w-[600px] flex-col items-center justify-center p-10 text-center">
        <AlertTriangle width={64} height={64} className="fill-on-background" />

        <p className="mb-3 mt-6 text-lg font-semibold leading-6">
          {t('something_went_wrong', { defaultValue: 'Something went wrong.' })}
        </p>

        <p className="text-base font-normal leading-5">
          {t('reload_and_try_again', { defaultValue: 'Reload the page and try again.' })}
        </p>

        <LoadingButtonLink
          linkProps={{
            href: qrId ? `/qr/${qrId}` : '/',
            className: 'w-full',
          }}
          buttonProps={{
            className: 'w-full font-subtitle-m [&]:text-subtitle-m text-on-primary mt-10',
            onClick: () => {
              window.location.reload();
            },
          }}
          buttonClasses={{ children: 'flex items-center justify-center gap-2' }}
        >
          <RotateCw className="fill-on-primary" />
          {t('reload', { defaultValue: 'Reload' })}
        </LoadingButtonLink>
        <Track name="Something Went Wrong" />
      </div>
    </CampaignBackground>
  );
}
